<div class="flex flex-col gap-4">
    @if (canUpload()) {
    <div class="flex justify-end">
        <p-button
            label="Generate for all templates"
            icon="pi pi-microchip-ai"
            size="small"
            severity="secondary"
            outlined
            [disabled]="compatibleCategory() === null"
            (onClick)="openBulk()"
        />
    </div>
    }
    <!-- Site template -->
    <div class="flex flex-col gap-2">
        <label class="font-medium"
            >Site template <span class="text-red-500">*</span></label
        >
        <p-select
            [options]="siteTemplateGroups()"
            [group]="true"
            optionLabel="label"
            optionValue="value"
            optionGroupLabel="label"
            optionGroupChildren="items"
            [ngModel]="selectedSiteTemplateId()"
            (ngModelChange)="selectedSiteTemplateId.set($event ?? null)"
            [loading]="siteTemplateService.loading()"
            [disabled]="!hasSiteTemplates()"
            [placeholder]="
                hasSiteTemplates()
                    ? 'Select a site template'
                    : 'No compatible site templates'
            "
            [filter]="true"
            filterBy="label"
            appendTo="body"
            [style]="{ width: '100%' }"
        />
        <small class="text-xs text-muted-color">
            @if (filterHint()) { {{ filterHint() }} } @else { Base art overrides
            how this game template renders inside the chosen site template. }
        </small>
    </div>

    <!-- Slots -->
    @if (selectedSiteTemplateId()) {
    <div
        class="flex flex-col gap-3 pt-3 border-t border-surface-200 dark:border-surface-700"
    >
        <div class="flex items-center justify-between gap-2 flex-wrap">
            <div class="flex flex-col">
                <label class="font-medium">Art slots</label>
                <small class="text-xs text-muted-color"
                    >Changes are saved automatically.</small
                >
            </div>
            <span [pTooltip]="aiReason() ?? ''" tooltipPosition="left">
                <p-button
                    label="Generate icon + banner with AI"
                    icon="pi pi-microchip-ai"
                    size="small"
                    severity="secondary"
                    outlined
                    [disabled]="aiDisabled() || loading() || anyBusy()"
                    (onClick)="openAi(['icon', 'banner'])"
                />
            </span>
        </div>

        @if (loading()) {
        <div class="flex items-center gap-2 text-muted-color text-sm py-4">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading base art…</span>
        </div>
        } @else { @for (slot of slots(); track slot.name) {
        <div
            class="flex items-start gap-3 rounded border border-surface-200 dark:border-surface-700 p-3"
        >
            <!-- Preview -->
            <div
                class="w-20 h-20 shrink-0 rounded border border-surface-200 dark:border-surface-700 flex items-center justify-center overflow-hidden bg-surface-50 dark:bg-surface-800"
            >
                @if (slot.busy) {
                <i class="pi pi-spin pi-spinner text-muted-color"></i>
                } @else if (slot.preview) {
                <img
                    [src]="slot.preview"
                    alt="Slot preview"
                    class="w-full h-full object-cover cursor-pointer"
                    title="View large"
                    (click)="preview.open(slot.preview, slot.name)"
                />
                } @else {
                <i class="pi pi-image text-muted-color text-xl"></i>
                }
            </div>

            <!-- Name + actions -->
            <div class="flex flex-col gap-2 flex-1 min-w-0">
                <div class="flex items-center gap-2">
                    <span class="font-medium capitalize">{{ slot.name }}</span>
                    @if (!slot.value) {
                    <span class="text-xs text-muted-color italic">empty</span>
                    }
                </div>
                <div class="flex items-center gap-2 flex-wrap">
                    <p-button
                        [label]="slot.value ? 'Replace image' : 'Upload image'"
                        icon="pi pi-upload"
                        size="small"
                        severity="secondary"
                        outlined
                        [loading]="slot.busy"
                        [disabled]="!canUpload()"
                        [pTooltip]="
                            canUpload()
                                ? ''
                                : 'Only a global admin can upload files.'
                        "
                        (onClick)="fileInput.click()"
                    />
                    <input
                        #fileInput
                        type="file"
                        accept="image/*"
                        class="hidden"
                        (change)="onFilePicked(slot.name, $event.target)"
                    />
                    @if (gamePlateType(slot.name); as plate) {
                    <span [pTooltip]="aiReason() ?? 'Generate with AI'">
                        <p-button
                            label="AI"
                            icon="pi pi-microchip-ai"
                            size="small"
                            severity="secondary"
                            outlined
                            [disabled]="aiDisabled() || slot.busy"
                            (onClick)="openAi([plate])"
                        />
                    </span>
                    } @if (slot.value) {
                    <p-button
                        icon="pi pi-trash"
                        size="small"
                        severity="danger"
                        text
                        [disabled]="slot.busy"
                        pTooltip="Clear image"
                        (onClick)="clearSlot(slot.name)"
                    />
                    }
                </div>
                @if (slot.value) {
                <small class="text-xs text-muted-color break-all">{{
                    slot.value
                }}</small>
                }
            </div>
        </div>
        } @empty {
        <div class="text-sm text-muted-color italic py-2">
            No art slots.
        </div>
        } }
    </div>
    } @if (compatibleCategory() === null) {
    <p-message
        severity="warn"
        text="This game's win mode is unresolved, so no compatible site template can be determined."
    />
    }
</div>

<app-ai-base-art-generate
    [visible]="aiVisible()"
    (visibleChange)="aiVisible.set($event)"
    [assetTypes]="aiAssetTypes()"
    [gameTemplateId]="template()?.Id ?? ''"
    [gameKind]="aiGameKind()"
    [gameName]="template()?.Title ?? ''"
    [gameDescription]="template()?.Description ?? ''"
    [templateId]="selectedSiteTemplateId() ?? ''"
    [version]="brandKit()?.version ?? ''"
    [references]="brandKit()?.references ?? []"
    [style]="brandKit()?.style ?? null"
    (applied)="onAiApplied($event)"
/>

<app-ai-base-art-bulk
    [visible]="bulkVisible()"
    (visibleChange)="bulkVisible.set($event)"
    [template]="template()"
    (changed)="onBulkChanged()"
/>
